/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Typography, Box, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LanguageContext } from '../../../../../../../libs/Context';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormState } from './hooks/useFormState';
import { useUIState } from './hooks/useUIState';
import { useMediaState } from './hooks/useMediaState';
import { validateForm } from './utils/validators';
import { deleteFileFromS3, submitCategory, updateCategoryData } from './utils/api';
import CategorySelector from './components/CategorySelector';
import FormFields from './components/FormFields';
import ThumbnailPreview from './components/ThumbnailPreview';
import UploadButton from './components/UploadButton';
import PermissionToggles from './components/PermissionToggles';
import UploadDialog from '../Reusable/UploadDialog';
import NewDialogPopup from '../../../../../../../components/Common/NewDialogPopup/NewDialogPopup';
import CropDialog from '../Reusable/RightBarOptions/Components/SliderContent/Components/CropDialog';
import { updatePlaylist, createPlaylist } from '../../../../../../actions/playlist_management';

interface CategorySliderProps {

	toggleCategory?: (obj?: any) => void;
	categoryType: 'playlist' | 'category' | 'subcategory' | 'categoryData';
	sliderEdit?: boolean;
	category?: any;
	contentData?: any;
	setCategories?: (data: any) => void;
	setCategoryData?: (data: any) => void;
	setOpen?: () => void;
	selectedTab?: string;
	// setSelectedTab?: (tab: string) => void;
	playlistData?: any;
	onSave?: (data: any) => void;
}

// Add a helper function to check if string has actual content
const hasValidContent = (str: string) => str.trim().length > 0;

const CategorySlider: React.FC<CategorySliderProps> = ({
	toggleCategory,
	categoryType,
	sliderEdit,
	category,
	contentData,
	setCategories,
	setCategoryData,
	setOpen,
	selectedTab,
	// setSelectedTab,
	playlistData,
	onSave
}) =>
{
	// Get form, UI, and media state from custom hooks
	const { formData, handleFormUpdate } = useFormState(playlistData || category);

	const { uiState, handleUIUpdate } = useUIState();
	const { mediaState, handleMediaUpdate } = useMediaState(category);

	// Get language context
	const { language } = useContext(LanguageContext);

	// Get company info from Redux state
	const companyId = useSelector((state: any) => state.mysession.session?.companyId);
	const companyName = useSelector((state: any) => state.mysession.session?.companyName);
	const isSubcategory = categoryType === 'subcategory' || category?.parentCategoryId || category?.isSubcategory;

	// Component local state
	const [ selectedParentCategoryId, setSelectedParentCategoryId ] = useState<string | null>(category?.parentCategoryId || null);
	const [ selectedParentLevel, setSelectedParentLevel ] = useState<number | null>(null);
	const [ selectedParentCategoryName, setSelectedParentCategoryName ] = useState<string>('');
	const [ isSubmitting, setIsSubmitting ] = useState(false);
	const [ specificUsers, setSpecificUsers ] = useState<any[]>([]);
	const [ isDeleting, setIsDeleting ] = useState(false);
	const [ selectedDepartments, setSelectedDepartments ] = useState<string[]>([]);
	const [ selectedRelations, setSelectedRelations ] = useState<string[]>([]);
	const [ isAllEmployeesSelected, setIsAllEmployeesSelected ] = useState(false);

	const contentManagement = useSelector((state: any) => state?.mysession?.configs?.contentManagement);

	console.log('contentManagement', contentManagement);

	// Initialize access control data when editing
	useEffect(() =>
	{
		if (sliderEdit && (category?.accessControl || playlistData?.accessControl))
		{
			const accessControl = category?.accessControl || playlistData?.accessControl;

			// Set internal employees data
			if (accessControl?.internalEmployees)
			{
				setIsAllEmployeesSelected(accessControl.internalEmployees.allSelected || false);
				setSelectedDepartments(
					accessControl.internalEmployees.departments?.map((dept) => dept.name) || []
				);
				setSpecificUsers(
					accessControl.internalEmployees.specificUsers?.map((user) => ({
						userId : user.userId,
						name   : user.name,
						email  : user.email,
						role   : user.role
					})) || []
				);
			}

			// Set user groups data
			if (accessControl?.userGroups)
			{
				setSelectedRelations(
					accessControl.userGroups.map((group) => group.type) || []
				);
			}
		}
	}, [ sliderEdit, category, playlistData ]);

	// Handler for parent category selection
	const handleParentCategorySelect = useCallback((categoryId: string, categoryData?: any) =>
	{
		setSelectedParentCategoryId(categoryId);
		setSelectedParentLevel(categoryData?.level || 0);
		if (categoryData?.name)
		{
			setSelectedParentCategoryName(categoryData.name);
		}
	}, []);

	// Handlers for participants selection
	const handleSpecificUsersUpdate = useCallback((users: any[]) =>
	{
		setSpecificUsers(users);
	}, []);

	const handleDepartmentsUpdate = useCallback((categoryLabel: string, selectedChips: Record<string, string[]>) =>
	{
		if (categoryLabel === 'Internal Employees')
		{
			const deptNames = selectedChips['Internal Employees'] || [];
			// Check if "All" is selected

			if (deptNames.includes('All'))
			{
				setIsAllEmployeesSelected(true);
				setSelectedDepartments([]);
			}
			else
			{
				setIsAllEmployeesSelected(false);
				setSelectedDepartments(deptNames);
			}
		}
	}, []);

	const handleRelationsUpdate = useCallback((relations: string[]) =>
	{
		setSelectedRelations(relations);
	}, []);

	// Effect to open category selector if creating a subcategory
	useEffect(() =>
	{
		if (isSubcategory)
		{
			handleUIUpdate('isCategorySelectOpen', true);
		}
	}, [ isSubcategory, handleUIUpdate ]);

	// Handle save operation
	const handleSave = useCallback(async () =>
	{
		// Check for valid title content
		if (!hasValidContent(formData.title))
		{
			toast.error('Please enter a valid title');

			return;
		}

		if (categoryType === 'playlist')
		{
			try
			{
				setIsSubmitting(true);
				if (sliderEdit && playlistData?._id)
				{
					// Determine thumbnailUrl value: if deleting (had thumbnail but now undefined), set to null
					const thumbnailUrlValue = (playlistData?.thumbnailUrl && !mediaState.thumbnailUrl)
						? null
						: mediaState.thumbnailUrl;

					// Update existing playlist
					const playlistPayload = {
						name          : formData.title.trim(),
						description   : formData.description,
						thumbnailUrl  : thumbnailUrlValue,
						enabled       : formData.enabled,
						allowShare    : formData.allowShare,
						visibility    : formData.visibility,
						accessControl : formData.visibility === 'private'
							? {
								internalEmployees : {
									allSelected   : isAllEmployeesSelected,
									departments   : selectedDepartments.map((dept) => ({ name: dept })),
									specificUsers : specificUsers.map((user) => ({
										userId : user.userId,
										name   : user.name,
										email  : user.email,
										role   : user.role || 'viewer'
									}))
								},
								userGroups : selectedRelations.map((relation) => ({
									type : relation
								}))
							}
							: undefined
					};

					if (formData.visibility === 'private' && (!specificUsers || specificUsers.length === 0) &&
					!isAllEmployeesSelected && selectedDepartments.length === 0 && selectedRelations.length === 0)
					{
						toast.error('Please select at least one user, department, or business relation for private visibility');

						return false;
					}
					const updatedPlaylist = await updatePlaylist(playlistData._id, playlistPayload);

					const refreshEvent = new CustomEvent('contentStatusChanged', {
						detail : {
							playlistId  : updatedPlaylist?._id,
							contentType : 'playlists',
							status      : 'updated'
						}
					});

					window.dispatchEvent(refreshEvent);

					onSave?.(updatedPlaylist);
				}
				else if (onSave)
				{
					// Content card context - use callback
					onSave({
						...formData,
						title : formData.title.trim()
					});
				}
				else
				{
					// Playlist creation
					const playlistPayload = {
						name          : formData.title.trim(),
						description   : formData.description,
						companyId,
						companyName,
						thumbnailUrl  : mediaState.thumbnailUrl,
						visibility    : formData.visibility,
						enabled       : formData.enabled,
						allowShare    : formData.allowShare,
						accessControl : formData.visibility === 'private'
							? {
								internalEmployees : {
									allSelected   : isAllEmployeesSelected,
									departments   : selectedDepartments.map((dept) => ({ name: dept })),
									specificUsers : specificUsers.map((user) => ({
										userId : user.userId,
										name   : user.name,
										email  : user.email,
										role   : user.role || 'viewer'
									}))
								},
								userGroups : selectedRelations.map((relation) => ({
									type : relation
								}))
							}
							: undefined
					};

					if (formData.visibility === 'private' && (!specificUsers || specificUsers.length === 0) &&
			!isAllEmployeesSelected && selectedDepartments.length === 0 && selectedRelations.length === 0)
					{
						toast.error('Please select at least one user, department, or business relation for private visibility');

						return false;
					}

					const newPlaylist = await createPlaylist(playlistPayload);

					const refreshEvent = new CustomEvent('contentStatusChanged', {
						detail : {
							playlistId  : newPlaylist?._id,
							contentType : 'playlists',
							status      : 'added'
						}
					});

					window.dispatchEvent(refreshEvent);

					// Check if playlist was created successfully
					if (newPlaylist && newPlaylist._id)
					{
						toggleCategory?.();
					}
				}
				setOpen?.();
			}
			catch (error)
			{
				console.error('Error saving playlist:', error);
			}
			finally
			{
				setIsSubmitting(false);
			}

			return;
		}

		// Validate form
		const isValid = validateForm(
			formData,
			isSubcategory,
			selectedParentCategoryId,
			selectedParentLevel,
			specificUsers,
			isAllEmployeesSelected,
			selectedDepartments,
			selectedRelations,
			sliderEdit
		);

		if (!isValid) return;

		if (!companyId)
		{
			toast.error('Company ID is required to create a category');

			return;
		}

		// Get the current toggle states right before payload creation
		const currentAllowShare = formData.allowShare;

		// Prepare the access control structure according to API documentation
		let accessControl: any;

		if (formData.visibility === 'private')
		{
			// Initialize access control object
			accessControl = {};

			// Add internal employees section if needed
			if (isAllEmployeesSelected || selectedDepartments.length > 0 || specificUsers.length > 0)
			{
				accessControl.internalEmployees = {
					allSelected   : isAllEmployeesSelected,
					departments   : selectedDepartments.map((dept) => ({ name: dept })),
					specificUsers : specificUsers.map((user) => ({
						userId : user.userId,
						name   : user.name,
						email  : user.email,
						role   : user.role || 'viewer'
					}))
				};
			}

			// Add user groups (relations) if any are selected
			if (selectedRelations.length > 0)
			{
				accessControl.userGroups = selectedRelations.map((relation) => ({
					type : relation
				}));
			}
		}

		// Determine thumbnailUrl value: if deleting (had thumbnail but now undefined), set to null
		// Otherwise use the mediaState value (which could be a URL or undefined for new categories)
		const thumbnailUrlValue = (sliderEdit && category?.thumbnailUrl && !mediaState.thumbnailUrl)
			? null
			: mediaState.thumbnailUrl;

		// Prepare the payload for category/subcategory
		const payload = {
			name             : formData.title,
			description      : formData.description,
			companyId,
			companyName,
			thumbnailUrl     : thumbnailUrlValue,
			thumbnailSource  : mediaState.thumbnailSource,
			thumbnailKey     : mediaState.thumbnailKey,
			isEnabled        : formData.enabled,
			...(!isSubcategory && { visibility: formData.visibility }),
			allowShare       : currentAllowShare,
			parentCategoryId : isSubcategory ? selectedParentCategoryId : null,
			...(!isSubcategory && { accessControl })
		};

		try
		{
			if (sliderEdit && category?._id)
			{
				// Update existing category
				const updatedCategory = await updateCategoryData(category._id, payload, setIsSubmitting);

				// Update the categories list with the updated category
				setCategoryData?.(updatedCategory);
				toggleCategory?.();
			}
			else
			{
				const createdCategory = await submitCategory(
					payload,
					categoryType,
					setIsSubmitting,
					toggleCategory
				);

				// 🔔 Fire event ONLY for subcategory creation
				if (categoryType === 'subcategory')
				{
					const subCategoryCreatedEvent = new CustomEvent('subcategoryCreated', {
						detail : {
							parentCategoryId : selectedParentCategoryId,
							categoryData     : createdCategory,
							timestamp        : Date.now()
						}
					});

					window.dispatchEvent(subCategoryCreatedEvent);
				}
			}
		}
		catch (error)
		{
			console.error('Error saving category:', error);
			// Error handling is done in the respective API functions
		}
	}, [
		formData,
		categoryType,
		selectedParentCategoryId,
		selectedParentLevel,
		specificUsers,
		isAllEmployeesSelected,
		selectedDepartments,
		selectedRelations,
		companyId,
		companyName,
		mediaState,
		toggleCategory,
		sliderEdit,
		category,
		setCategories,
		setCategoryData,
		onSave,
		setOpen
	]);

	// Handler for file selection in upload dialog
	const handleFileSelect = useCallback((url: string, source?: string, key?: string) =>
	{
		// Set replacing thumbnail state to suppress delete toast
		handleUIUpdate('isReplacingThumbnail', true);

		// If we're replacing an existing thumbnail from S3, delete the old one without showing toast
		if (mediaState.hasThumbnail && mediaState.thumbnailSource === 'system' && mediaState.thumbnailKey)
		{
			// Create a wrapper function that doesn't show toast
			const deleteWithoutToast = (loading: boolean) =>
			{
				setIsDeleting(loading);
			};

			deleteFileFromS3(mediaState.thumbnailKey, deleteWithoutToast);
		}
		handleMediaUpdate(url, source, key);
		handleUIUpdate('isReplacingThumbnail', false);
	}, [ mediaState, handleMediaUpdate, handleUIUpdate, setIsDeleting ]);

	// Handler for thumbnail deletion
	const handleDeleteThumbnail = useCallback(async () =>
	{
		// Check if the thumbnail is from S3 and has a key
		if (mediaState.thumbnailSource === 'system' && mediaState.thumbnailKey)
		{
			await deleteFileFromS3(mediaState.thumbnailKey, setIsDeleting);
		}

		// Reset thumbnail state
		handleMediaUpdate(undefined);
		handleUIUpdate('isDeleteDialogOpen', false);
	}, [ mediaState, handleMediaUpdate, handleUIUpdate ]);

	// Handler for crop thumbnail
	const handleCropThumbnail = useCallback(() =>
	{
		// Check if there's a thumbnail to crop
		if (!mediaState.thumbnailUrl)
		{
			toast.warning('Please upload a thumbnail first before cropping');

			return;
		}

		// Open crop dialog
		handleUIUpdate('isCropDialogOpen', true);
	}, [ mediaState.thumbnailUrl, handleUIUpdate ]);

	// Handler for crop completion
	const handleCropComplete = useCallback((croppedImageUrl: string, croppedImageKey?: string) =>
	{
		// Set replacing thumbnail state to suppress delete toast
		handleUIUpdate('isReplacingThumbnail', true);

		// If we're replacing an existing thumbnail from S3, delete the old one without showing toast
		if (mediaState.thumbnailSource === 'system' && mediaState.thumbnailKey)
		{
			// Create a wrapper function that doesn't show toast
			const deleteWithoutToast = (loading: boolean) =>
			{
				setIsDeleting(loading);
			};

			deleteFileFromS3(mediaState.thumbnailKey, deleteWithoutToast);
		}

		// Update thumbnail with cropped image
		handleMediaUpdate(croppedImageUrl, 'system', croppedImageKey);

		// Reset replacing thumbnail state
		handleUIUpdate('isReplacingThumbnail', false);
		handleUIUpdate('isCropDialogOpen', false);
	}, [ mediaState, handleUIUpdate, deleteFileFromS3, handleMediaUpdate, setIsDeleting ]);

	// Handler for crop cancellation
	const handleCropCancel = useCallback(() =>
	{
		handleUIUpdate('isCropDialogOpen', false);
	}, [ handleUIUpdate ]);

	// Get the header title based on type and edit mode
	const getHeaderTitle = () =>
	{
		if (categoryType === 'playlist')
		{
			return sliderEdit ? 'Edit Playlist' : 'Create Playlist';
		}

		return sliderEdit
			? (isSubcategory ? 'Edit Subcategory' : 'Edit Category')
			: isSubcategory ? language?.subCat : language?.category;
	};

	// Check if we have a thumbnail from playlist or category
	const existingThumbnail = useMemo(() =>
	{
		if (categoryType === 'playlist')
		{
			return playlistData?.thumbnailUrl || playlistData?.thumbnail?.url;
		}

		return mediaState?.thumbnailUrl;
	}, [ categoryType, playlistData, mediaState ]);

	// Initialize media state with existing thumbnail
	useEffect(() =>
	{
		if (existingThumbnail)
		{
			const thumnailKey = existingThumbnail.split('https://devuploads.inlynk.com/')[1];

			handleMediaUpdate(existingThumbnail, mediaState?.thumbnailSource, thumnailKey);
		}
	}, [ existingThumbnail, handleMediaUpdate ]);

	return (
		<Grid container height={'100%'} direction={'column'} onClick={(e) => e.stopPropagation()}>
			<Grid item px={2} py={1}>
				<Box display='flex' justifyContent='space-between' alignItems='center'>
					<Typography fontWeight={'var(--font500)'} fontSize={'var(--pixel20)'}>
						{getHeaderTitle()}
					</Typography>
					<IconButton onClick={toggleCategory || setOpen}>
						<CloseIcon />
					</IconButton>
				</Box>
			</Grid>

			<Grid item minHeight={0} px={2} py={1} xs sx={{ overflowY: 'auto' }}>
				{!sliderEdit && isSubcategory && (
					<CategorySelector
						isOpen={uiState.isCategorySelectOpen}
						onToggle={() => handleUIUpdate('isCategorySelectOpen', !uiState.isCategorySelectOpen)}
						onSelectCategory={handleParentCategorySelect}
						initialCategoryName={selectedParentCategoryName}
						selectedCategoryId={selectedParentCategoryId || undefined}
					/>
				)}

				<FormFields
					formData={formData}
					onUpdate={handleFormUpdate}
					categoryType={categoryType}
					language={language}
					category={categoryType === 'playlist' ? playlistData : category}
					isSubcategory={isSubcategory}
				/>

				{mediaState.hasThumbnail || existingThumbnail
					? (
						<ThumbnailPreview
							thumbnailUrl={mediaState.thumbnailUrl || existingThumbnail}
							onHover={(hover) => handleUIUpdate('isHovering', hover)}
							onReplace={() => handleUIUpdate('isUploadDialogOpen', true)}
							onDelete={() => handleUIUpdate('isDeleteDialogOpen', true)}
							onCrop={handleCropThumbnail}
						/>
					)
					: (
						<UploadButton onClick={() => handleUIUpdate('isUploadDialogOpen', true)} />
					)}

				<PermissionToggles
					formData={formData}
					onUpdate={handleFormUpdate}
					isSubcategory={isSubcategory}
					language={language}
					isAllEmployeesSelected={isAllEmployeesSelected}
					onSpecificUsersUpdate={handleSpecificUsersUpdate}
					onDepartmentsUpdate={handleDepartmentsUpdate}
					onRelationsUpdate={handleRelationsUpdate}
					specificUsers={specificUsers}
					selectedDepartments={selectedDepartments}
					selectedRelations={selectedRelations}
					sliderEdit={sliderEdit}
				/>
			</Grid>

			<Grid item p={2}>
				<Box display='flex' justifyContent='flex-end' gap={2}>
					<Button variant='outlined' onClick={toggleCategory || setOpen} disabled={isSubmitting}>
						{language?.cancel}
					</Button>
					<Button
						variant='contained'
						sx={{
							color         : 'var(--whiteColor)',
							opacity       : (hasValidContent(formData.title) && !isSubmitting) ? '' : 0.6,
							pointerEvents : (hasValidContent(formData.title) && !isSubmitting) ? 'all' : 'none'
						}}
						onClick={handleSave}
						disabled={!hasValidContent(formData.title) || isSubmitting}
					>
						{isSubmitting ? 'Saving...' : sliderEdit ? 'Update' : 'Save'}
					</Button>
				</Box>
			</Grid>

			<UploadDialog
				open={uiState.isUploadDialogOpen}
				onClose={() => handleUIUpdate('isUploadDialogOpen', false)}
				fileType='Image'
				note={language?.supportformate}
				onFileSelect={handleFileSelect}
				Embednote='Only Upload JPEG, JPG and PNG link Formats.'
				fromCreation='category'
			/>

			{uiState.isDeleteDialogOpen && (
				<NewDialogPopup
					open={uiState.isDeleteDialogOpen}
					handleConfirm={() => handleUIUpdate('isDeleteDialogOpen', false)}
					type={language?.no}
					id={undefined}
					label={`${language?.LikeToDeleteThe} thumbnail?`}
					from='feedCreate'
					formatType={language?.yes}
					closeCallBack={handleDeleteThumbnail}
				/>
			)}

			{/* Crop Dialog */}
			<CropDialog
				open={uiState.isCropDialogOpen}
				imageUrl={mediaState.thumbnailUrl || ''}
				onClose={handleCropCancel}
				onCropComplete={handleCropComplete}
				language={language}
			/>
		</Grid>
	);
};

export default CategorySlider;